export const companyInfo = {
  name: "The WE Mentality",
  program: "Culture Align",
  tagline: "Leadership Development and Workforce Training That Improves Performance, Retention, and Results",
  description: "We help organizations develop stronger leaders, align teams, and integrate AI and modern workforce strategies.",
  website: "www.thewementality.com",
  logo: "/brand/the-we-mentality.png"
};

export const trustedOrganizations = [
  { name: "UNLV", type: "Education" },
  { name: "Clark County School District", type: "Education" },
  { name: "Veterans Business Outreach Center", type: "Government" },
  { name: "City of Las Vegas", type: "Government" },
  { name: "Urban Chamber of Commerce", type: "Business" },
  { name: "United States Air Force", type: "Government" },
  { name: "Nevada Partners", type: "Workforce" }
];

export const measurableOutcomes = [
  { label: "Employee Engagement", description: "Teams that are motivated, invested, and committed to organizational success" },
  { label: "Leadership Effectiveness", description: "Leaders who communicate clearly, make decisions confidently, and develop others" },
  { label: "Communication Clarity", description: "Reduced friction and misunderstandings across teams and departments" },
  { label: "Retention & Morale", description: "Lower turnover and higher job satisfaction among team members" },
  { label: "Team Alignment", description: "Shared goals, clear expectations, and collaborative execution" },
  { label: "Productivity & Execution", description: "Improved output, efficiency, and follow-through on commitments" }
];

export const challenges = [
  {
    title: "Leadership Gaps & Inconsistent Management",
    description: "Leaders promoted for technical skills often lack the training needed to engage, develop, and retain their teams effectively.",
    icon: "Users"
  },
  {
    title: "Poor Communication Across Teams",
    description: "Siloed departments and unclear communication create friction, delays, and missed opportunities for collaboration.",
    icon: "MessageSquareOff"
  },
  {
    title: "Low Employee Engagement & Accountability",
    description: "Without clear expectations and ownership, performance varies widely and morale suffers.",
    icon: "Target"
  },
  {
    title: "Difficulty Adapting to Change & Technology",
    description: "Resistance to change and skill gaps slow down implementation of new systems, processes, and tools.",
    icon: "RefreshCw"
  },
  {
    title: "Lack of Workforce Readiness & Growth",
    description: "Teams lack the professional development needed to advance, adapt, and contribute at higher levels.",
    icon: "TrendingUp"
  },
  {
    title: "Culture Misalignment & Performance Gaps",
    description: "When values, behavior, and expectations don't align, organizations struggle to achieve consistent results.",
    icon: "Layers"
  },
  {
    title: "Uncertainty Around AI Implementation",
    description: "Organizations want to leverage AI but don't know how to implement it effectively, responsibly, or strategically.",
    icon: "Bot"
  }
];

export const solutions = [
  {
    id: 1,
    title: "Leadership Development",
    icon: "Crown",
    description: "Build confident, capable leaders who inspire teams and drive results.",
    features: [
      "Executive and management coaching",
      "Communication and accountability training",
      "Decision-making frameworks",
      "Leadership presence and influence",
      "Emerging leader development"
    ]
  },
  {
    id: 2,
    title: "Workforce Development",
    icon: "Users",
    description: "Strengthen your workforce with skills that improve performance and retention.",
    features: [
      "Upskilling and career readiness",
      "Team effectiveness training",
      "Workplace communication",
      "Frontline development",
      "Retention and engagement support"
    ]
  },
  {
    id: 3,
    title: "Professional Development",
    icon: "GraduationCap",
    description: "Develop the soft skills and professional behaviors that elevate performance.",
    features: [
      "Customer service excellence",
      "Emotional intelligence",
      "Collaboration and teamwork",
      "Confidence and presentation skills",
      "Time management and productivity"
    ]
  },
  {
    id: 4,
    title: "AI Integration & Readiness",
    icon: "Bot",
    description: "Prepare your organization to leverage AI responsibly and effectively.",
    features: [
      "AI readiness assessments",
      "Productivity tool training",
      "Workflow enhancement",
      "AI for communication and planning",
      "Responsible AI implementation"
    ]
  },
  {
    id: 5,
    title: "Culture & Team Alignment",
    icon: "Heart",
    description: "Create alignment between people, values, and organizational goals.",
    features: [
      "DISC and behavioral assessments",
      "Team dynamics workshops",
      "Conflict reduction strategies",
      "Values and trust alignment",
      "Collaboration frameworks"
    ]
  },
  {
    id: 6,
    title: "Custom Training & Consulting",
    icon: "Settings",
    description: "Tailored solutions designed for your unique organizational needs.",
    features: [
      "Custom workshop design",
      "Strategic facilitation",
      "Organizational consulting",
      "Keynote and speaking",
      "Program development"
    ]
  }
];

export const processPhases = [
  {
    phase: 1,
    title: "Discover",
    description: "Assess needs, goals, leadership challenges, team dynamics, and organizational opportunities.",
    outcomes: ["Needs assessment", "Goal alignment", "Gap identification", "Opportunity mapping"]
  },
  {
    phase: 2,
    title: "Design",
    description: "Build a customized development strategy using workshops, coaching, assessments, and AI tools.",
    outcomes: ["Custom strategy", "Program design", "Resource planning", "Timeline development"]
  },
  {
    phase: 3,
    title: "Deliver",
    description: "Facilitate engaging training, coaching, and development experiences that create practical results.",
    outcomes: ["Interactive sessions", "Skill application", "Team engagement", "Real-time feedback"]
  },
  {
    phase: 4,
    title: "Sustain",
    description: "Provide follow-up support, accountability tools, and recommendations for long-term impact.",
    outcomes: ["Ongoing support", "Progress tracking", "Reinforcement", "Next steps planning"]
  }
];

export const results = [
  { label: "Stronger Leadership Capacity", description: "Leaders who communicate, decide, and execute with confidence" },
  { label: "Improved Communication", description: "Teams that collaborate effectively across departments" },
  { label: "Higher Engagement & Accountability", description: "People who own outcomes and follow through" },
  { label: "AI & Technology Readiness", description: "Organizations prepared to leverage modern tools" },
  { label: "Increased Professionalism", description: "Elevated skills and workplace behaviors" },
  { label: "Greater Alignment", description: "People, performance, and purpose working together" },
  { label: "Better Retention & Morale", description: "Teams that stay, grow, and contribute" },
  { label: "Sustainable Growth", description: "Systems that support long-term success" }
];

export const programOptions = [
  {
    id: 1,
    title: "Workshops & Training Sessions",
    description: "Single sessions or multi-session series for teams, staff, leaders, or students.",
    icon: "Presentation"
  },
  {
    id: 2,
    title: "Leadership & Team Development",
    description: "Structured short-term or long-term development engagements.",
    icon: "Users"
  },
  {
    id: 3,
    title: "Assessments & Debriefs",
    description: "DISC, motivators, emotional intelligence, and team insights.",
    icon: "ClipboardCheck"
  },
  {
    id: 4,
    title: "AI Readiness Training",
    description: "Practical training on responsible AI use to improve workflows and growth.",
    icon: "Bot"
  },
  {
    id: 5,
    title: "Strategic Consulting",
    description: "Customized support for workforce initiatives, retreats, and planning.",
    icon: "Compass"
  },
  {
    id: 6,
    title: "Partnership Programs",
    description: "Solutions for workforce boards, schools, chambers, and agencies.",
    icon: "Handshake"
  }
];

export const audiences = [
  { name: "Businesses & Entrepreneurs", icon: "Building2" },
  { name: "Corporate Teams", icon: "Briefcase" },
  { name: "Manufacturing & Operations", icon: "Factory" },
  { name: "Schools & Educators", icon: "School" },
  { name: "Colleges & Universities", icon: "GraduationCap" },
  { name: "Government & Public Sector", icon: "Landmark" },
  { name: "Workforce Development Orgs", icon: "Users" },
  { name: "Nonprofits & Community Orgs", icon: "Heart" }
];

export const aiFeatures = [
  "AI readiness workshops for leaders and teams",
  "AI-powered productivity and workflow training",
  "Responsible AI use in the workplace",
  "AI tools for communication, planning, and growth",
  "Bridging people development with technology adoption",
  "Custom AI integration strategies"
];

export const serviceTypes = [
  { value: "leadership", label: "Leadership Development" },
  { value: "team", label: "Team Training" },
  { value: "workforce-funded", label: "Workforce Funded Programs" },
  { value: "ai", label: "AI and Workplace Productivity" },
  { value: "assessments", label: "Assessments and Team Insights" },
  { value: "speaking", label: "Speaking or Workshops" },
  { value: "partnership", label: "Partnership Opportunities" }
];

export const stats = [
  { value: "500+", label: "Leaders Developed" },
  { value: "100+", label: "Organizations Served" },
  { value: "95%", label: "Client Satisfaction" },
  { value: "12+", label: "Years Experience" }
];

export const testimonials = [
  {
    quote: "The WE Mentality transformed how our leadership team communicates and collaborates. The results were immediate and lasting.",
    author: "Operations Director",
    org: "Regional Healthcare System"
  },
  {
    quote: "Their DISC training gave our team insights we'd never had before. It changed the way we work together.",
    author: "HR Manager",
    org: "Manufacturing Company"
  },
  {
    quote: "Practical, engaging, and directly applicable. This wasn't just another training—it was a real development experience.",
    author: "Executive Director",
    org: "Workforce Development Agency"
  }
];
